import { ReactNode, useMemo, useRef, useState } from 'react';
import { Box, Center, Flex, Grid } from '@chakra-ui/react';

import { FCWithChildren } from 'common/types';
import Button from 'components/_core/Button';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';

import { Content } from './components/Content';
import Item from './components/Item';
import { Plans } from './components/Plans';
import { Title } from './components/Title';
import Context from './Context';
import locales from './locales';

interface TopBarProps {
  onClose: () => void;
  onPrev: () => void;
  content?: React.ReactElement | null;
}

export const MOBILE_HEADER_HEIGHT = 44;

/**
 * This component is used to create the recap top bar on the pricing page.
 * Every card is injected using TopBar.Item
 */
const TopBar: FCWithChildren<TopBarProps> = ({ children, onClose, onPrev }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [nbPlans, setNbPlans] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<ReactNode>(null);
  const containerRef = useRef(null);
  const [x, setX] = useState(0);
  const width = nbPlans * 292 + (nbPlans - 1) * 32;

  const contextValue = useMemo(
    () => ({
      containerRef,
      onPositionChange: setX,
      setContent,
      setIsExpanded,
      setNbPlans,
      setTitle,
    }),
    [],
  );

  return (
    <Context.Provider value={contextValue}>
      <Box
        _after={{
          backdropFilter: `${isExpanded ? 'blur(16px)' : 'transparent'}`,
          backgroundColor: `${
            isExpanded ? 'rgba(255, 255, 255, 0.8)' : 'white'
          }`,
          borderBottom: '1px solid',
          borderColor: 'grey.4',
          content: '""',
          display: {
            base: 'none',
            lg: 'block',
          },
          height: 'full',
          left: 0,
          position: 'absolute',
          top: 0,
          transform: `translateY(${isExpanded ? 0 : 'calc(-100% + 4rem)'})`,
          transition: isExpanded
            ? 'transform 250ms cubic-bezier(0.4, 0.0, 1, 1), opacity 250ms cubic-bezier(0.4, 0.0, 1, 1)'
            : 'transform 200ms cubic-bezier(0.0, 0.0, 0.2, 1), opacity 200ms cubic-bezier(0.0, 0.0, 0.2, 1)',
          width: 'full',
          zIndex: -2,
        }}
        background={{
          base: 'white',
          lg: 'none',
        }}
        height={{
          base: MOBILE_HEADER_HEIGHT,
          lg: isExpanded ? 'auto' : '4rem',
        }}
        justifyContent="space-between"
        left={0}
        position="fixed"
        top={0}
        width="100vw"
        zIndex={2}
      >
        <Box
          display={{
            base: 'none',
            md: 'block',
          }}
          left="1.5rem"
          position="absolute"
          top="1rem"
        >
          <Button
            icon="cross"
            iconPosition="left"
            onClick={onClose}
            size="sm"
            variant="tertiary"
          >
            {locales.closeButtonText}
          </Button>
        </Box>
        <Flex
          alignItems="center"
          alignSelf="stretch"
          display={{
            base: 'flex',
            lg: 'none',
          }}
          height={MOBILE_HEADER_HEIGHT}
          justifyContent="space-between"
          left={0}
          paddingX="space-16"
          position="fixed"
          top={0}
          width="100vw"
        >
          <IconButton
            aria-label={locales.back}
            icon="arrow-left"
            // visually align the icon on the left
            iconSize="icon-24"
            marginLeft={-12.5}
            onClick={onPrev}
            variant="inline-secondary"
          />
          <Center flex={1} marginLeft="-space-32">
            <Text fontWeight={500} variant="primary">
              {title}
            </Text>
          </Center>
          {/* <Button onClick={intercom.show} variant="inline-primary">
            {locales.help}
          </Button> */}
        </Flex>
        {content ? (
          <Flex
            alignItems="center"
            height="4rem"
            left={`${x}px`}
            position="fixed"
            top={0}
            width={`${width}px}`}
            zIndex={2}
          >
            {content}
          </Flex>
        ) : null}
        <Box
          marginLeft={`${x}px`}
          paddingTop={content ? '4rem' : 0}
          transform={`translateY(${isExpanded ? 0 : '-100%'})`}
          transition={
            isExpanded
              ? 'transform 250ms cubic-bezier(0.4, 0.0, 1, 1), opacity 250ms cubic-bezier(0.4, 0.0, 1, 1)'
              : 'transform 200ms cubic-bezier(0.0, 0.0, 0.2, 1), opacity 200ms cubic-bezier(0.0, 0.0, 0.2, 1)'
          }
        >
          <Grid
            gap="space-40"
            gridTemplateColumns={`repeat(${nbPlans}, 1fr)`}
            ref={containerRef}
            width={`${width}px}`}
          />
        </Box>
      </Box>
      {children}
    </Context.Provider>
  );
};

export default Object.assign(TopBar, {
  Content,
  Item,
  Plans,
  Title,
});
